import { onUnmounted, watch } from "vue";
import { useEplexAccountInfoStore } from "../../store/eplex/eplexAccountInfo";
import { useOnboardingStore } from "../../store/onboarding";
import { useIdentityChange } from "./useIdentityChange";
import { useOnboardingRewardWatcher } from "./useOnboardingRewardWatcher";

/**
 * Право видеть онбординг: квесты, награды и их опрос (CAB-733, CAB-734).
 *
 * Единственное место, где это право включается и выключается. Признак участия
 * приезжает асинхронно и умеет меняться под открытой вкладкой, поэтому не
 * разовая проверка на входе, а наблюдение — с обеими сторонами:
 *
 *   признак появился   — грузим чек-лист и запускаем наградный опрос;
 *   признак снят       — опрос останавливаем, чек-лист забываем. Иначе вкладка,
 *                        открытая с вечера, показывает утром награды, которых
 *                        человеку уже (или вообще никогда) не полагалось;
 *   сменилась личность — сведения прежнего человека не наследуются: признак
 *                        гасится сразу и запрашивается заново, и до ответа
 *                        онбординга нет ни у кого.
 */
export function useOnboardingAccess(): void {
  const accountInfo = useEplexAccountInfoStore();
  const onboarding = useOnboardingStore();
  const rewardWatcher = useOnboardingRewardWatcher();

  watch(
    () => accountInfo.isOnboardingParticipant,
    (isParticipant, wasParticipant) => {
      if (isParticipant) {
        onboarding.load();
        rewardWatcher.start();
        return;
      }

      // Первое же срабатывание с `false` — это ещё не снятие признака, а
      // обычное состояние «сведения не приехали»: гасить нечего
      if (!wasParticipant) return;

      rewardWatcher.stop();
      onboarding.reset();
    },
    { immediate: true },
  );

  useIdentityChange(() => {
    rewardWatcher.stop();
    onboarding.reset();
    // Сброс до запроса, а не после: пока новый ответ в пути, признака нет ни у
    // кого — показать чужой онбординг хуже, чем на секунду не показать свой
    accountInfo.reset();
    accountInfo.load();
  });

  onUnmounted(() => rewardWatcher.stop());
}
